/** @jsxImportSource theme-ui */
import React, { memo, useState, useEffect } from "react";
import { useWindowWidth } from "@react-hook/window-size/throttled";
import {
  useStackedPagesProvider,
  StackedPagesProvider,
  useStackedPages
} from "../react-stacked-pages-hook";
import { dataToNote, dataToSlug } from "../utils/data-to-note";
import Note from "./note";
import NoteWrapper from "./note-wrapper";
import styled from '@emotion/styled';
import { Box } from "theme-ui";

import "./theme.css";
import "./stacked-layout.css";
import "./custom.css";

const Content = ({ windowWidth, scrollContainer, stackedPages, index }) => {
  return (
    <StyledBox className="layout">
      <div className="note-columns-scrolling-container" ref={scrollContainer}>
        <div
          className="note-columns-container"
          style={{ width: 625 * (stackedPages.length + 1) }}
        >
          {stackedPages.map((page, i) => (
            <NoteWrapper
              key={page.slug}
              i={typeof index !== "undefined" ? index : i}
              slug={page.slug}
              title={page.data.title}
            >
              <Note {...page.data} />
            </NoteWrapper>
          ))}
        </div>
      </div>
    </StyledBox>
  );
};
const MemoContent = memo(Content);

const NotesLayout = ({ location, slug, data }) => {
  const windowWidth = useWindowWidth();

  const [state, scrollContainer] = useStackedPagesProvider({
    firstPage: { slug: dataToSlug(data), data },
    location,
    processPageQuery: dataToNote,
    pageWidth: 625,
  });

  let pages = state.stackedPages;

  let activeIndex;

  if (windowWidth <= 800) {
    const activeSlug = Object.keys(state.stackedPageStates).find(
      (slug) => state.stackedPageStates[slug].active
    );
    activeIndex = state.stackedPages.findIndex(
      (page) => page.slug === activeSlug
    );
    if (activeIndex === -1) {
      activeIndex = state.stackedPages.length - 1;
    }

    pages = [state.stackedPages[activeIndex]];
  }

  return (
    <StackedPagesProvider value={state}>
      <MemoContent
        windowWidth={windowWidth}
        scrollContainer={scrollContainer}
        stackedPages={pages}
        index={activeIndex}
      />
    </StackedPagesProvider>
  );
};

const StyledBox = styled(Box)`
	h1 {
		font-size: 2.188rem;
		font-weight: 600;
    margin-bottom: 1.25rem;
	}

	h2 {
		font-size: 1.875rem;
		font-weight: 600;
    margin-top: 2.5rem;
		margin-bottom: 1.25rem;
	}

	h3 {
		font-size: 1.25rem;
		font-weight: 700;
		line-height: 32.5px;
	}

	h5 {
		font-size: 1.125rem;
		font-weight: 400;
		line-height: 29.25px;
	}

	p {
		font-size: 1.125rem !important;
		line-height: 36px !important;
    margin-bottom: 1.25rem !important;
	}

  *,
  :after,
  :before {
    box-sizing: border-box;
  }

  a {
    color: ${(props) => props.theme.colors.primary};
    text-decoration: none;
    font-size: 1rem;
  }
  a:hover {
    text-decoration: underline;
  }

  .note-container  {
    a {
      text-decoration: none;
      transition: color 0.3s ease;
      font-size: 1.125rem;
      color: #AA00EA;
    }
  
    a:hover {
      text-decoration: underline;
    }
  }

	ul {
		font-size: 1.25rem;
		line-height: 32.5px;
		padding-left: 1.25rem;
		margin-left: 1.25rem;
		margin-bottom: 1.25rem;
	}

	li {
		font-size: 1.125rem;
		line-height: 29.25px;
		margin-bottom: 0.625rem;
	}

	li > a {
		font-size: 1.125rem;
	}

	.references-block {
		margin: 1.25rem 0;
		padding: 1.25rem;
    a {
      text-decoration: none;
      color: #718096;
    }
	}

	.popover {
		max-width: 20rem;
		padding: 1rem;
		max-height: 16rem;
	}

	.popover.no-max-width {
		max-width: 90vw;
	}

	.popover.with-markdown {
		font-size: 1rem;
	}

	.popover h1 {
		margin: 0;
		padding: 0;
		font-size: 1.25rem !important;
	}

	.popover ul {
		padding-left: 1rem;
	}

	.popover .more-content-blind {
		height: 4rem;
	}
  .note-columns-scrolling-container {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    flex-grow: 1;
  }
  .layout {
    height: 100vh;
    display: flex;
    flex-direction: column;
  }
  
  .note-columns-scrolling-container {
    display: flex;
    overflow-x: auto;
    overflow-y: hidden;
    flex-grow: 1;
  }
  
  .note-columns-container {
    display: flex;
    flex-grow: 1;
    transition: width 100ms cubic-bezier(0.19, 1, 0.22, 1);
  }
  
  @media screen and (max-width: 800px) {
    .note-columns-container {
      width: unset !important;
    }
  }
`;


export default NotesLayout;
